import React, { useEffect, useState, useRef } from "react";
import classes from './AssetDisposal.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, FileText, Calendar, DollarSign, Trash, TrendingUp, TrendingDown } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const AssetDisposal = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_disposals: 0,
        total_value: 0,
        total_gain: 0,
        total_loss: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [assetDisposals, setAssetDisposals] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedDisposal, setSelectedDisposal] = useState(null);

    // Create Modal state
    const [assets, setAssets] = useState([]);
    const [createFormData, setCreateFormData] = useState({
        asset_id: "",
        date_disposed: "",
        disposal_method: "",
        disposal_value: "",
        gain_or_loss: "",
        journal_entry_id: "",
        company_id: ""
    });
    const [createLoading, setCreateLoading] = useState({
        assets: false
    });
    const [submitting, setSubmitting] = useState(false);

    // For searchable selects in create modal
    const [searchTerms, setSearchTerms] = useState({
        asset: ""
    });
    const [showDropdowns, setShowDropdowns] = useState({
        asset: false
    });

    // Create refs for each dropdown
    const assetRef = useRef(null);

    // Disposal method options
    const disposalMethods = [
        { value: 'SOLD', label: 'Sold' },
        { value: 'SCRAPPED', label: 'Scrapped' },
        { value: 'DONATED', label: 'Donated' }
    ];

    // Modal form data for view/edit
    const [modalData, setModalData] = useState({
        id: "",
        asset_id: "",
        asset_name: "",
        asset_code: "",
        date_disposed: "",
        disposal_method: "",
        disposal_method_label: "",
        disposal_value: "",
        gain_or_loss: "",
        gain_loss_type: "",
        journal_entry_id: "",
        company_id: ""
    });

    // Helper function to get company ID from any source
    const getCompanyIdFromStorage = () => {
        // First check direct company_id
        const directCompanyId = localStorage.getItem("company_id");
        if (directCompanyId) return directCompanyId;

        // Check companyInfo
        const companyInfo = localStorage.getItem("companyInfo");
        if (companyInfo) {
            try {
                const parsed = JSON.parse(companyInfo);
                const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                if (id) return String(id);
            } catch (error) {
                console.error("Error parsing companyInfo:", error);
            }
        }

        // Check user data
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const parsed = JSON.parse(userData);
                if (parsed.company_id) return String(parsed.company_id);
                if (parsed.company) {
                    const id = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                    if (id) return String(id);
                }
                if (parsed.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0) {
                    const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                    if (mainCompany) {
                        const id = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                        if (id) return String(id);
                    }
                    const firstCompany = parsed.companies[0];
                    const id = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                    if (id) return String(id);
                }
            } catch (error) {
                console.error("Error parsing user data:", error);
            }
        }

        return null;
    };

    // Get company ID from localStorage
    useEffect(() => {
        const id = getCompanyIdFromStorage();
        if (id) {
            console.log("Found company ID:", id);
            setCompanyId(id);
            setCreateFormData(prev => ({
                ...prev,
                company_id: id
            }));
        } else {
            console.warn("No company ID found in any source");
        }
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assetRef.current && !assetRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, asset: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch data for create modal when opened
    useEffect(() => {
        if (showCreateModal) {
            const validCompanyId = getCompanyIdFromStorage();
            if (validCompanyId) {
                if (!companyId) setCompanyId(validCompanyId);
                fetchAssets(validCompanyId);
            } else {
                console.warn("Cannot fetch assets: Company ID not available");
                Swal.fire({
                    icon: 'warning',
                    title: 'Company ID Missing',
                    text: 'Please select a company and try again.',
                    confirmButtonText: 'Go to Dashboard'
                }).then(() => {
                    setShowCreateModal(false);
                    navigate('/dashboard');
                });
            }
        }
    }, [showCreateModal]);

    // Fetch asset disposals when companyId is available
    useEffect(() => {
        if (companyId) {
            console.log("Company ID available, fetching asset disposals for company:", companyId);
            fetchAssetDisposals();
        } else {
            console.warn("Company ID not available yet, waiting...");
        }
    }, [companyId]);

    const fetchAssetDisposals = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = companyId || getCompanyIdFromStorage();

            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            if (!validCompanyId) {
                console.error("Company ID is required");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please select a company and try again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching asset disposals for company ID: ${validCompanyId}`);

            const response = await axios.get(`${BASE_URL}/v1/assetdisposed`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    company_id: validCompanyId
                }
            });

            console.log("Asset disposals response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssetDisposals(response.data.data);

                // Update metrics
                let totalValue = 0;
                let totalGain = 0;
                let totalLoss = 0;

                response.data.data.forEach(disp => {
                    totalValue += parseFloat(disp.disposal_value) || 0;
                    const gainLoss = parseFloat(disp.gain_or_loss) || 0;
                    if (gainLoss > 0) {
                        totalGain += gainLoss;
                    } else {
                        totalLoss += Math.abs(gainLoss);
                    }
                });

                setSelectedMetrics({
                    total_disposals: response.data.data.length,
                    total_value: totalValue,
                    total_gain: totalGain,
                    total_loss: totalLoss
                });

                console.log(`Loaded ${response.data.data.length} asset disposals`);
            } else {
                console.warn("Unexpected response format:", response.data);
                setAssetDisposals([]);
            }
        } catch (error) {
            console.error("Error fetching asset disposals:", error);

            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);

                let errorMessage = "Failed to fetch asset disposals";
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 401) {
                    errorMessage = "Unauthorized. Please log in again.";
                } else if (error.response.status === 404) {
                    errorMessage = "API endpoint not found";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            } else if (error.request) {
                console.error("No response received:", error.request);
                Swal.fire({
                    icon: 'error',
                    title: 'Network Error',
                    text: 'No response from server. Please check your connection.'
                });
            } else {
                console.error("Error setting up request:", error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred while setting up the request'
                });
            }

            setAssetDisposals([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssets = async (companyIdParam = null) => {
        setCreateLoading(prev => ({ ...prev, assets: true }));
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = companyIdParam || companyId || getCompanyIdFromStorage();

            if (!validCompanyId) {
                console.error("Cannot fetch assets: No company ID");
                setCreateLoading(prev => ({ ...prev, assets: false }));
                return;
            }

            const response = await axios.get(`${BASE_URL}/v1/fixedassets/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });

            console.log("Assets response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                const assetsData = response.data.data;

                // Log the first asset to see available fields
                if (assetsData.length > 0) {
                    console.log("Sample asset structure:", assetsData[0]);
                    console.log("Available fields:", Object.keys(assetsData[0]));
                }

                // Filter assets that are available for disposal
                const availableAssets = assetsData.filter(asset => {
                    // Check if asset has been disposed already
                    // You might have a field like is_disposed or disposed_status
                    // For now, show all assets that aren't marked as disposed
                    if (asset.is_disposed === true || asset.is_disposed === 1) {
                        return false;
                    }

                    // Check net book value - if it's 0 or less, asset is fully depreciated
                    const netBookValue = asset.net_book_value ||
                        asset.current_value ||
                        asset.book_value ||
                        asset.nbv ||
                        0;

                    // Only show assets with positive net book value
                    // or show all if net book value field doesn't exist
                    if (netBookValue !== undefined && netBookValue !== null) {
                        return parseFloat(netBookValue) > 0;
                    }

                    return true;
                });

                setAssets(availableAssets);
                console.log(`Loaded ${availableAssets.length} assets available for disposal out of ${assetsData.length} total assets`);

                if (availableAssets.length === 0 && assetsData.length > 0) {
                    Swal.fire({
                        icon: 'info',
                        title: 'No Assets Available',
                        text: 'All assets have been fully depreciated or already disposed. Please add new assets first.',
                        confirmButtonText: 'OK'
                    });
                }
            } else {
                console.warn("Unexpected assets response format:", response.data);
                setAssets([]);
            }
        } catch (error) {
            console.error("Error fetching assets:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch assets. Please try again.'
            });
        } finally {
            setCreateLoading(prev => ({ ...prev, assets: false }));
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    // Process disposal data for modal
    const processDisposalData = (disposal) => {
        console.log("Processing disposal data:", disposal);
        const gainLoss = parseFloat(disposal.gain_or_loss) || 0;
        const gainLossType = gainLoss > 0 ? "Gain" : gainLoss < 0 ? "Loss" : "No Gain/Loss";

        // Find method label
        const method = disposalMethods.find(m => m.value === disposal.disposal_method);
        const methodLabel = method ? method.label : disposal.disposal_method;

        setModalData({
            id: disposal.id,
            asset_id: disposal.asset_id,
            asset_name: disposal.fixed_asset?.asset_name || "N/A",
            asset_code: disposal.fixed_asset?.asset_code || disposal.asset_code || "N/A",
            date_disposed: disposal.date_disposed || "",
            disposal_method: disposal.disposal_method || "",
            disposal_method_label: methodLabel,
            disposal_value: disposal.disposal_value || "",
            gain_or_loss: disposal.gain_or_loss || "",
            gain_loss_type: gainLossType,
            journal_entry_id: disposal.journal_entry_id || "",
            company_id: disposal.company_id || ""
        });
    };

    // Handle View/Edit icon clicks
    const handleViewClick = async (disposal) => {
        setModalMode("view");
        setSelectedDisposal(disposal);
        setModalLoading(true);
        setShowViewEditModal(true);
        processDisposalData(disposal);
        setModalLoading(false);
    };

    const handleEditClick = async (disposal) => {
        setModalMode("edit");
        setSelectedDisposal(disposal);
        setModalLoading(true);
        setShowViewEditModal(true);
        processDisposalData(disposal);
        setModalLoading(false);
    };

    // Handle modal input changes for view/edit
    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle create form changes
    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle searchable select for create modal
    const handleSearchChange = (field, e) => {
        const value = e.target.value;
        setSearchTerms(prev => ({ ...prev, [field]: value }));

        // If the user is typing, clear the selected asset_id if it doesn't match
        if (createFormData.asset_id) {
            const selectedAsset = assets.find(asset => asset.id === createFormData.asset_id);
            const selectedDisplay = selectedAsset ? `${selectedAsset.asset_name} (${selectedAsset.asset_code})` : "";
            if (selectedDisplay !== value) {
                setCreateFormData(prev => ({ ...prev, asset_id: "" }));
            }
        }

        setShowDropdowns(prev => ({ ...prev, [field]: true }));
    };

    const handleSelect = (field, item) => {
        setCreateFormData(prev => ({
            ...prev,
            asset_id: item.id
        }));

        const displayName = `${item.asset_name} (${item.asset_code})`;
        setSearchTerms(prev => ({ ...prev, asset: displayName }));
        setShowDropdowns(prev => ({ ...prev, asset: false }));
    };

    const getSelectedName = (field) => {
        const id = createFormData.asset_id;
        if (!id) return "";

        const item = assets.find(i => i.id === id);
        if (!item) return "";

        return `${item.asset_name} (${item.asset_code})`;
    };

    const filterItems = (field) => {
        const searchTerm = (searchTerms.asset || "").toLowerCase();

        if (!Array.isArray(assets) || assets.length === 0) {
            return [];
        }

        return assets.filter(item => {
            if (!item) return false;

            const assetName = (item.asset_name || "").toLowerCase();
            const assetCode = (item.asset_code || "").toLowerCase();
            return assetName.includes(searchTerm) || assetCode.includes(searchTerm);
        });
    };

    // Handle save for edit mode with all required fields
    const handleSave = async () => {
        if (modalMode === "edit") {
            setSaveLoading(true);
            try {
                const token = localStorage.getItem("authToken");

                // Double-check company ID
                let currentCompanyId = modalData.company_id || companyId || getCompanyIdFromStorage();

                if (!currentCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please select a company and try again.'
                    });
                    setSaveLoading(false);
                    return;
                }

                // Prepare request data with ALL required fields
                const requestData = {
                    id: selectedDisposal.id,
                    asset_id: modalData.asset_id,
                    date_disposed: modalData.date_disposed,
                    disposal_method: modalData.disposal_method,
                    disposal_value: modalData.disposal_value,
                    gain_or_loss: modalData.gain_or_loss,
                    journal_entry_id: modalData.journal_entry_id || null,
                    company_id: currentCompanyId
                };

                console.log("Updating asset disposal with data:", requestData);

                const response = await axios.post(
                    `${BASE_URL}/v1/assetdisposed/edit`,
                    requestData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                console.log("Update response:", response.data);

                if (response.data.status) {
                    await fetchAssetDisposals();
                    setShowViewEditModal(false);

                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: response.data.message || 'Asset disposal updated successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    let errorMessage = "Failed to update asset disposal";
                    if (response.data.message) {
                        if (typeof response.data.message === 'object') {
                            errorMessage = Object.values(response.data.message).flat().join(', ');
                        } else {
                            errorMessage = response.data.message;
                        }
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: errorMessage
                    });
                }
            } catch (error) {
                console.error("Error updating asset disposal:", error);

                let errorMessage = "Failed to update asset disposal";

                if (error.response) {
                    if (error.response.data && error.response.data.message) {
                        if (typeof error.response.data.message === 'object') {
                            errorMessage = Object.values(error.response.data.message).flat().join(', ');
                        } else {
                            errorMessage = error.response.data.message;
                        }
                    } else if (error.response.status === 422) {
                        errorMessage = "Validation error. Please check all required fields.";
                    } else if (error.response.status === 404) {
                        errorMessage = "Asset disposal record not found.";
                    }
                } else if (error.request) {
                    errorMessage = "No response from server. Please check your connection.";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setSaveLoading(false);
            }
        } else {
            setShowViewEditModal(false);
        }
    };

    // Handle create submit
    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.asset_id ||
            !createFormData.date_disposed ||
            !createFormData.disposal_method ||
            !createFormData.disposal_value ||
            !createFormData.gain_or_loss) {
            Swal.fire("Error", "Please fill in all required fields", "error");
            return;
        }

        // Double-check company ID
        let currentCompanyId = companyId || getCompanyIdFromStorage();

        if (!currentCompanyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please select a company and try again.',
                confirmButtonText: 'Go to Dashboard'
            }).then(() => {
                navigate('/dashboard');
            });
            return;
        }

        if (!companyId) {
            setCompanyId(currentCompanyId);
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                asset_id: createFormData.asset_id,
                date_disposed: createFormData.date_disposed,
                disposal_method: createFormData.disposal_method,
                disposal_value: createFormData.disposal_value,
                gain_or_loss: createFormData.gain_or_loss,
                journal_entry_id: createFormData.journal_entry_id || null,
                company_id: currentCompanyId
            };

            console.log("Creating asset disposal with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/v1/assetdisposed/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("Create response:", response.data);

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Asset disposal recorded successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });

                resetCreateForm();
                onClose();
                await refreshDisposals();
            } else {
                Swal.fire("Error", response.data?.message || "Failed to record asset disposal", "error");
            }
        } catch (error) {
            console.error("Error creating asset disposal:", error);

            let errorMessage = "Something went wrong while recording the disposal";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message).flat().join(', ');
                } else {
                    errorMessage = error.response.data.message;
                }
            }

            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete disposal
    const handleDeleteClick = async (disposal) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                const validCompanyId = companyId || getCompanyIdFromStorage();

                if (!validCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Company ID not found.'
                    });
                    return;
                }

                console.log(`Deleting asset disposal with ID: ${disposal.id}, Company ID: ${validCompanyId}`);

                const response = await axios.post(
                    `${BASE_URL}/v1/assetdisposed/delete`,
                    {
                        id: disposal.id,
                        company_id: validCompanyId
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                console.log("Delete response:", response.data);

                if (response.data.status) {
                    await fetchAssetDisposals();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Asset disposal deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete asset disposal");
                }
            } catch (error) {
                console.error("Error deleting asset disposal:", error);

                let errorMessage = "Failed to delete asset disposal";
                if (error.response && error.response.data && error.response.data.message) {
                    if (typeof error.response.data.message === 'object') {
                        errorMessage = Object.values(error.response.data.message).flat().join(', ');
                    } else {
                        errorMessage = error.response.data.message;
                    }
                } else if (error.response && error.response.status === 404) {
                    errorMessage = "Asset disposal record not found";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const resetCreateForm = () => {
        const validCompanyId = companyId || getCompanyIdFromStorage();
        setCreateFormData({
            asset_id: "",
            date_disposed: "",
            disposal_method: "",
            disposal_value: "",
            gain_or_loss: "",
            journal_entry_id: "",
            company_id: validCompanyId || ""
        });
        setSearchTerms({
            asset: ""
        });
    };

    const onClose = () => {
        setShowCreateModal(false);
        resetCreateForm();
        setShowDropdowns({
            asset: false
        });
    };

    const refreshDisposals = async () => {
        await fetchAssetDisposals();
    };

    // Format currency
    const formatCurrency = (value) => {
        if (!value) return "0.00";
        return parseFloat(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString();
    };

    // Get method label
    const getMethodLabel = (method) => {
        const found = disposalMethods.find(m => m.value === method);
        return found ? found.label : method;
    };

    // Skeleton loader for table rows
    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    // Skeleton for modal form inputs
    const ModalInputSkeleton = () => (
        <div className={classes.skeletonInput}></div>
    );

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/transactions/sales-invoice/sale')}>
                            Sales Invoice
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/Inventory/PurchaseOrder')}>
                            Purchase Order
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Stock Delivery
                        </button>
                        <button onClick={() => navigate('/settings/product-services')} className={`${classes.btns} ${classes.greens}`}>
                            Product & Services
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Asset Disposal</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Disposals</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_disposals?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Disposed assets</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Value</p>
                                    <p className={classes.kdptg2}>
                                        {formatCurrency(selectedMetrics?.total_value)}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Disposal value</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.fiftkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Gain</p>
                                    <p className={classes.kdptg3}>
                                        {formatCurrency(selectedMetrics?.total_gain)}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Profit on disposal</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frstkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Loss</p>
                                    <p className={classes.kdptg4}>
                                        {formatCurrency(selectedMetrics?.total_loss)}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Loss on disposal</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search disposals" />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select className={classes.dropdown}>
                                    <option value="">All Methods</option>
                                    {disposalMethods.map(method => (
                                        <option key={method.value} value={method.value}>{method.label}</option>
                                    ))}
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>
                            + Record Disposal
                        </button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Asset</th>
                                <th>Date Disposed</th>
                                <th>Method</th>
                                <th>Disposal Value</th>
                                <th>Gain/Loss</th>
                                <th>Type</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : assetDisposals.length > 0 ? (
                                assetDisposals.map((disposal, index) => {
                                    const gainLoss = parseFloat(disposal.gain_or_loss) || 0;
                                    const isGain = gainLoss > 0;
                                    const isLoss = gainLoss < 0;

                                    return (
                                        <tr key={disposal.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                {disposal.fixed_asset?.asset_name || "—"}
                                                {disposal.fixed_asset?.asset_code && <div className={classes.smallText}>{disposal.fixed_asset.asset_code}</div>}
                                            </td>
                                            <td>{formatDate(disposal.date_disposed)}</td>
                                            <td>{getMethodLabel(disposal.disposal_method)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(disposal.disposal_value)}</td>
                                            <td style={{
                                                textAlign: 'right',
                                                color: isGain ? '#16A34A' : isLoss ? '#DC2626' : 'inherit',
                                                fontWeight: '500'
                                            }}>
                                                {isGain ? '+' : ''}{formatCurrency(disposal.gain_or_loss)}
                                            </td>
                                            <td>
                                                <span className={isGain ? classes.gainBadge : isLoss ? classes.lossBadge : classes.neutralBadge}>
                                                    {isGain ? 'Gain' : isLoss ? 'Loss' : 'No Gain/Loss'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                    <Eye
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#007bff" }}
                                                        onClick={() => handleViewClick(disposal)}
                                                    />
                                                    <Pencil
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#28a745" }}
                                                        onClick={() => handleEditClick(disposal)}
                                                    />
                                                    <Trash2
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#dc3545" }}
                                                        onClick={() => handleDeleteClick(disposal)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                                        {loading ? "Loading..." : "No asset disposal records found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Trash className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Disposal Record" : "View Disposal Record"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid}>
                            {modalLoading ? (
                                Array.from({ length: 6 }).map((_, index) => (
                                    <div className={classes.formGroup} key={index}>
                                        <div className={classes.skeletonLabel}></div>
                                        <ModalInputSkeleton />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Asset</label>
                                        <input
                                            type="text"
                                            value={`${modalData.asset_name} (${modalData.asset_code})`}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Date Disposed *</label>
                                        <input
                                            type={modalMode === "edit" ? "date" : "text"}
                                            value={modalData.date_disposed}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("date_disposed", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2}
                                            required={modalMode === "edit"}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Disposal Method *</label>
                                        {modalMode === "edit" ? (
                                            <select
                                                value={modalData.disposal_method}
                                                onChange={(e) => handleModalChange("disposal_method", e.target.value)}
                                                className={classes.Field2}
                                                disabled={saveLoading}
                                                required
                                            >
                                                <option value="">Select Method</option>
                                                {disposalMethods.map(method => (
                                                    <option key={method.value} value={method.value}>{method.label}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={modalData.disposal_method_label}
                                                readOnly
                                                className={classes.Field2}
                                            />
                                        )}
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Disposal Value *</label>
                                        <input
                                            type="number"
                                            value={modalData.disposal_value}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("disposal_value", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2}
                                            min="0"
                                            step="0.01"
                                            style={{ textAlign: 'right' }}
                                            required={modalMode === "edit"}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Gain/Loss *</label>
                                        <input
                                            type="number"
                                            value={modalData.gain_or_loss}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("gain_or_loss", e.target.value)}
                                            className={`${classes.number2} ${parseFloat(modalData.gain_or_loss) > 0 ? classes.gain : parseFloat(modalData.gain_or_loss) < 0 ? classes.loss : ''}`}
                                            step="0.01"
                                            style={{ textAlign: 'right' }}
                                            required={modalMode === "edit"}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Gain/Loss Type</label>
                                        <input
                                            type="text"
                                            value={modalData.gain_loss_type}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <input type="hidden" value={modalData.company_id} />
                                </>
                            )}
                        </div>

                        <button
                            className={classes.saveBtn}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <span className={classes.loadingText}>
                                    <span className={classes.spinner}></span>
                                    Saving...
                                </span>
                            ) : (
                                modalMode === "edit" ? "Update Record" : "Close"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Modal - Fixed Dropdown */}
            {showCreateModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Trash className={classes.icon} />
                                <h2>Record Asset Disposal</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className={classes.formGrid}>
                                <div className={classes.formGroup}>
                                    <label>Select Asset *</label>
                                    <div className={classes.searchSelectWrapper} ref={assetRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select asset..."
                                                value={showDropdowns.asset ? searchTerms.asset : (getSelectedName('asset') || searchTerms.asset)}
                                                onChange={(e) => handleSearchChange('asset', e)}
                                                onClick={() => {
                                                    setShowDropdowns(prev => ({ ...prev, asset: true }));
                                                    // If no search term and no selected asset, show all assets
                                                    if (!searchTerms.asset && !createFormData.asset_id) {
                                                        setSearchTerms(prev => ({ ...prev, asset: "" }));
                                                    }
                                                }}
                                                onFocus={() => {
                                                    setShowDropdowns(prev => ({ ...prev, asset: true }));
                                                }}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.asset && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.assets ? (
                                                        <div className={classes.dropdownItem}>Loading assets...</div>
                                                    ) : filterItems('asset').length === 0 ? (
                                                        <div className={classes.dropdownItem}>
                                                            {assets.length === 0 ? "No assets found" : "No matching assets found"}
                                                        </div>
                                                    ) : (
                                                        filterItems('asset').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.asset_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('asset', item)}
                                                            >
                                                                <strong>{item.asset_name}</strong> ({item.asset_code})
                                                                {item.net_book_value && (
                                                                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                                                                        - NBV: {formatCurrency(item.net_book_value)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {assets.length === 0 && !createLoading.assets && (
                                        <small className={classes.helperText} style={{ color: '#ff9800' }}>
                                            No active assets available for disposal. Please add assets first.
                                        </small>
                                    )}
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Disposal Date *</label>
                                    <input
                                        type="date"
                                        name="date_disposed"
                                        value={createFormData.date_disposed}
                                        onChange={handleCreateChange}
                                        required
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Disposal Method *</label>
                                    <select
                                        name="disposal_method"
                                        value={createFormData.disposal_method}
                                        onChange={handleCreateChange}
                                        required
                                    >
                                        <option value="">Select Method</option>
                                        {disposalMethods.map(method => (
                                            <option key={method.value} value={method.value}>{method.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Disposal Value *</label>
                                    <input
                                        type="number"
                                        name="disposal_value"
                                        placeholder="0.00"
                                        value={createFormData.disposal_value}
                                        onChange={handleCreateChange}
                                        min="0"
                                        step="0.01"
                                        required
                                        style={{ textAlign: 'right' }}
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Gain/Loss *</label>
                                    <input
                                        type="number"
                                        name="gain_or_loss"
                                        placeholder="0.00 (Positive for gain, negative for loss)"
                                        value={createFormData.gain_or_loss}
                                        onChange={handleCreateChange}
                                        step="0.01"
                                        required
                                        style={{ textAlign: 'right' }}
                                    />
                                    <small className={classes.helperText}>Positive for gain, negative for loss</small>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={classes.saveBtn}
                                disabled={submitting || createLoading.assets}
                            >
                                {submitting ? "Recording..." : "Record Disposal"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AssetDisposal;